import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { ColDef } from 'ag-grid-community';
import {
  termsApi,
  TERMS_STATUS_LABEL,
  TERMS_TARGET_LABEL,
  TERMS_TYPE_LABEL,
  type GetTermsParams,
  type Terms,
  type TermsTargetRole,
  type TermsType,
} from '@/services/api';
import {
  PageHeader,
  FilterBar,
  FilterField,
  Select,
  Pagination,
} from '@/components/common';
import {
  DataGrid,
  DateCell,
  makeBadgeCell,
  makeActionCell,
  RowNumberCell,
} from '@/components/grid';

type TargetFilter = TermsTargetRole | 'ALL';
type TypeFilter = TermsType | 'ALL';

const PAGE_SIZE_OPTIONS = [10, 30, 50, 100];

const formatDateTime = (dt?: string | null) =>
  dt ? dt.slice(0, 16).replace('T', ' ') : '-';

export function TermsListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [pageIndex, setPageIndex] = useState(0);
  const [size, setSize] = useState(10);
  const [appliedTarget, setAppliedTarget] = useState<TargetFilter>('ALL');
  const [appliedType, setAppliedType] = useState<TypeFilter>('ALL');

  const [localTarget, setLocalTarget] = useState<TargetFilter>('ALL');
  const [localType, setLocalType] = useState<TypeFilter>('ALL');
  const [localSize, setLocalSize] = useState(10);

  const queryParams = useMemo<GetTermsParams>(
    () => ({
      page: pageIndex,
      size,
      targetRole: appliedTarget,
      termsType: appliedType,
    }),
    [pageIndex, size, appliedTarget, appliedType],
  );

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['terms', queryParams],
    queryFn: () => termsApi.getTermsList(queryParams),
    placeholderData: prev => prev,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const rows = data?.content ?? [];
  const totalPages = data?.totalPages ?? 0;
  const totalElements = data?.totalElements ?? 0;
  const startIndex = pageIndex * size;

  const removeTerms = useMutation({
    mutationFn: (row: Terms) => termsApi.deleteTerms(row.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['terms'] });
    },
    onError: err => {
      alert(err instanceof Error ? err.message : '삭제에 실패했습니다.');
    },
  });

  const handleSearch = () => {
    setPageIndex(0);
    setSize(localSize);
    setAppliedTarget(localTarget);
    setAppliedType(localType);
  };

  const handleReset = () => {
    setLocalTarget('ALL');
    setLocalType('ALL');
    setLocalSize(10);
    setPageIndex(0);
    setSize(10);
    setAppliedTarget('ALL');
    setAppliedType('ALL');
  };

  const columns = useMemo<ColDef<Terms>[]>(
    () => [
      {
        headerName: '번호',
        width: 72,
        sortable: false,
        cellRenderer: RowNumberCell,
        cellRendererParams: { startIndex },
      },
      {
        headerName: '대상',
        field: 'targetRole',
        width: 96,
        cellRenderer: makeBadgeCell({
          toneMap: {
            GUARDIAN: 'success',
            BUSINESS: 'info',
            COMMON: 'neutral',
          },
          labelMap: TERMS_TARGET_LABEL,
        }),
      },
      {
        headerName: '유형',
        field: 'termsType',
        width: 150,
        valueGetter: p => (p.data ? TERMS_TYPE_LABEL[p.data.termsType] : ''),
      },
      {
        headerName: '제목',
        field: 'termsTitle',
        flex: 1,
        minWidth: 240,
        tooltipField: 'termsTitle',
      },
      { headerName: '버전', field: 'termsVersion', width: 100 },
      {
        headerName: '상태',
        field: 'status',
        width: 110,
        cellRenderer: makeBadgeCell({
          toneMap: {
            ACTIVE: 'success',
            SCHEDULED: 'warning',
            DRAFT: 'neutral',
            ARCHIVED: 'neutral',
          },
          labelMap: TERMS_STATUS_LABEL,
        }),
      },
      {
        headerName: '시행일',
        width: 160,
        valueGetter: p => (p.data ? formatDateTime(p.data.effectiveAt) : ''),
      },
      {
        headerName: '고지 시작일',
        width: 160,
        valueGetter: p => (p.data ? formatDateTime(p.data.noticeAt) : ''),
      },
      {
        headerName: '필수',
        field: 'isRequired',
        width: 84,
        cellRenderer: makeBadgeCell({
          toneMap: { true: 'info', false: 'neutral' },
          labelMap: { true: '필수', false: '선택' },
        }),
      },
      {
        headerName: '등록일',
        field: 'createdAt',
        width: 160,
        cellRenderer: DateCell,
      },
      {
        headerName: '관리',
        width: 250,
        sortable: false,
        cellRenderer: makeActionCell<Terms>([
          {
            label: '상세',
            onClick: row => navigate(`/terms/${row.id}`),
          },
          {
            label: '수정',
            onClick: row => navigate(`/terms/${row.id}/edit`),
            // 시행된 버전은 동의 증빙 원본이라 수정 불가 — 새 버전으로만 개정
            show: row => row.status === 'DRAFT' || row.status === 'SCHEDULED',
          },
          {
            label: '새 버전',
            onClick: row => navigate(`/terms/new?from=${row.id}`),
            show: row => row.status === 'ACTIVE' || row.status === 'ARCHIVED',
          },
          {
            label: '삭제',
            variant: 'danger',
            onClick: row => {
              if (
                window.confirm(
                  `약관을 삭제하시겠습니까?\n${row.termsTitle} (${row.termsVersion})`,
                )
              ) {
                removeTerms.mutate(row);
              }
            },
            show: row => row.status === 'DRAFT' || row.status === 'SCHEDULED',
          },
        ]),
      },
    ],
    [navigate, startIndex, removeTerms],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="약관 관리"
        subtitle="보호자·사업자 약관을 버전 단위로 관리합니다. 개정은 새 버전 등록으로만 가능하며, 시행된 버전은 동의 증빙 원본이라 수정·삭제할 수 없습니다."
        right={
          <button
            type="button"
            onClick={() => navigate('/terms/new')}
            className="whitespace-nowrap rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-600"
          >
            새 버전 등록
          </button>
        }
      />

      <FilterBar onSubmit={handleSearch} onReset={handleReset}>
        <FilterField flex="flex-none" className="md:w-40">
          <Select
            value={localTarget}
            onChange={e => setLocalTarget(e.target.value as TargetFilter)}
          >
            <option value="ALL">전체 대상</option>
            <option value="GUARDIAN">보호자</option>
            <option value="BUSINESS">사업자</option>
            <option value="COMMON">공통</option>
          </Select>
        </FilterField>
        <FilterField flex="flex-none" className="md:w-52">
          <Select
            value={localType}
            onChange={e => setLocalType(e.target.value as TypeFilter)}
          >
            <option value="ALL">전체 유형</option>
            <option value="TERMS_TYPE_SERVICE">이용약관</option>
            <option value="TERMS_TYPE_PRIVACY">개인정보처리방침</option>
            <option value="TERMS_TYPE_LOCATION">위치기반서비스</option>
            <option value="TERMS_TYPE_MARKETING">마케팅 수신동의</option>
          </Select>
        </FilterField>
        <FilterField flex="flex-none" className="md:w-28">
          <Select
            value={localSize}
            onChange={e => setLocalSize(Number(e.target.value))}
          >
            {PAGE_SIZE_OPTIONS.map(n => (
              <option key={n} value={n}>
                {n}개
              </option>
            ))}
          </Select>
        </FilterField>
      </FilterBar>

      <DataGrid<Terms>
        columns={columns}
        data={rows}
        loading={isLoading}
        fetching={isFetching && !isLoading}
        error={error}
        onRetry={() => refetch()}
        getRowId={row => String(row.id)}
        emptyMessage="등록된 약관이 없습니다."
      />

      <Pagination
        pageIndex={pageIndex}
        totalPages={totalPages}
        totalElements={totalElements}
        pageSize={size}
        onPageChange={setPageIndex}
      />
    </div>
  );
}
